import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'es' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.courses': 'Cursos',
    'nav.cart': 'Carrito',
    
    // Course cards
    'course.viewDetails': 'Ver Detalles',
    'course.addToCart': 'Agregar al Carrito',
    'course.lessons': 'lecciones',
    'course.students': 'estudiantes',
    'course.duration': 'duración',
    
    // Course detail
    'courseDetail.whatYouWillLearn': 'Lo que aprenderás',
    'courseDetail.requirements': 'Requisitos',
    'courseDetail.courseFeatures': 'Características del curso',
    'courseDetail.instructor': 'Instructor',
    'courseDetail.addToCart': 'Agregar al Carrito',
    'courseDetail.preview': 'Vista Previa',
    'courseDetail.duration': 'Duración',
    'courseDetail.level': 'Nivel',
    'courseDetail.lessons': 'Lecciones',
    'courseDetail.certificate': 'Certificado',
    'courseDetail.lifetimeAccess': 'Acceso de por vida',
    'courseDetail.yes': 'Sí',
    'courseDetail.no': 'No',
    
    // Cart
    'cart.empty': 'Tu carrito está vacío',
    'cart.emptyDescription': 'Agrega algunos cursos para comenzar tu aprendizaje',
    'cart.exploreCourses': 'Explorar Cursos',
    'cart.title': 'Carrito de Compras',
    'cart.clearCart': 'Vaciar carrito',
    'cart.quantity': 'Cantidad',
    'cart.subtotal': 'Subtotal',
    'cart.taxes': 'Impuestos',
    'cart.shipping': 'Envío',
    'cart.free': 'Gratis',
    'cart.total': 'Total',
    'cart.proceedToPayment': 'Proceder al Pago',
    'cart.securePayment': 'Pago seguro con SSL',
    'cart.each': 'cada uno',
    
    // Common
    'common.currency': 'MXN',
    'common.currencySymbol': '$',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.courses': 'Courses',
    'nav.cart': 'Cart',
    
    // Course cards
    'course.viewDetails': 'View Details',
    'course.addToCart': 'Add to Cart',
    'course.lessons': 'lessons',
    'course.students': 'students',
    'course.duration': 'duration',
    
    // Course detail
    'courseDetail.whatYouWillLearn': 'What You\'ll Learn',
    'courseDetail.requirements': 'Requirements',
    'courseDetail.courseFeatures': 'Course Features',
    'courseDetail.instructor': 'Instructor',
    'courseDetail.addToCart': 'Add to Cart',
    'courseDetail.preview': 'Preview',
    'courseDetail.duration': 'Duration',
    'courseDetail.level': 'Level',
    'courseDetail.lessons': 'Lessons',
    'courseDetail.certificate': 'Certificate',
    'courseDetail.lifetimeAccess': 'Lifetime Access',
    'courseDetail.yes': 'Yes',
    'courseDetail.no': 'No',
    
    // Cart
    'cart.empty': 'Your cart is empty',
    'cart.emptyDescription': 'Add some courses to start your learning journey',
    'cart.exploreCourses': 'Explore Courses',
    'cart.title': 'Shopping Cart',
    'cart.clearCart': 'Clear cart',
    'cart.quantity': 'Quantity',
    'cart.subtotal': 'Subtotal',
    'cart.taxes': 'Taxes',
    'cart.shipping': 'Shipping',
    'cart.free': 'Free',
    'cart.total': 'Total',
    'cart.proceedToPayment': 'Proceed to Payment',
    'cart.securePayment': 'Secure payment with SSL',
    'cart.each': 'each',
    
    // Common
    'common.currency': 'MXN',
    'common.currencySymbol': '$',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('es');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations[typeof language]] || key;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 